import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./Dashboard.css";
import TopNavBar from "./TopNavBar";
import AlbumList from "./AlbumList";
import CreateAlbumModal from "./CreateAlbumModal";
import DeleteAlbumModal from "./DeleteAlbumModal";
import MergeSharesModal from "./MergeSharesModal";
import { ErrorBoundary } from "./shared";
import { logError } from "../utils/errorHandler";
import { useUser, useAlbums, useSharedAlbums } from "../hooks";
import { AlbumWithShare, CreateAlbumDto } from "../types/album";
import { authService } from "../services/authService";

type AlbumFilter = "all" | "my-albums" | "shared";

interface AlbumsPageProps {
  onLogout: () => void;
}

const AlbumsPage: React.FC<AlbumsPageProps> = ({ onLogout }) => {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [albumModalTab, setAlbumModalTab] = useState<"create" | "join">("create");
  const [albumToDelete, setAlbumToDelete] = useState<AlbumWithShare | null>(null);
  const [activeFilter, setActiveFilter] = useState<AlbumFilter>("all");
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [isMerging, setIsMerging] = useState(false);
  const [anonymousShareCount, setAnonymousShareCount] = useState(0);
  const navigate = useNavigate();
  const isAnonymous = authService.isAnonymousUser();

  const { user, isLoading: userLoading } = useUser();
  const {
    albums: ownedAlbums,
    hasAlbums,
    isLoading: albumsLoading,
    isCreating,
    isDeleting,
    error: albumsError,
    createAlbum,
    deleteAlbum,
    clearError,
  } = useAlbums(user?.id);

  const {
    sharedAlbumsWithDetails,
    isLoading: sharedLoading,
    error: sharedError,
  } = useSharedAlbums(user?.id);

  const ownedWithMetadata: AlbumWithShare[] = useMemo(
    () =>
      ownedAlbums.map((album) => ({
        ...album,
        isShared: false,
      })),
    [ownedAlbums],
  );

  // Union for "All": one row per album (ownerId + id). Owned entry wins over a shared copy of the same album.
  const albumsForAllView = useMemo(() => {
    const byKey = new Map<string, AlbumWithShare>();
    for (const album of ownedWithMetadata) {
      byKey.set(`${album.ownerId}:${album.id}`, album);
    }
    for (const album of sharedAlbumsWithDetails) {
      const key = `${album.ownerId}:${album.id}`;
      if (!byKey.has(key)) {
        byKey.set(key, album);
      }
    }
    return Array.from(byKey.values());
  }, [ownedWithMetadata, sharedAlbumsWithDetails]);

  const filteredAlbums = useMemo(() => {
    switch (activeFilter) {
      case "my-albums":
        return ownedWithMetadata;
      case "shared":
        return sharedAlbumsWithDetails;
      case "all":
      default:
        return albumsForAllView;
    }
  }, [activeFilter, ownedWithMetadata, sharedAlbumsWithDetails, albumsForAllView]);

  const error = albumsError || sharedError;

  // Always open Albums page from the top for predictable navigation UX.
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);

  // Check for anonymous shares on mount (only for logged-in users)
  useEffect(() => {
    if (!isAnonymous && user?.id) {
      const tokens = authService.getAnonymousShareTokens();
      if (tokens.length > 0) {
        setAnonymousShareCount(tokens.length);
        setShowMergeModal(true);
      }
    }
  }, [isAnonymous, user?.id]);

  const handleMergeShares = async () => {
    setIsMerging(true);
    try {
      await authService.mergeAnonymousShares();
      setShowMergeModal(false);
      // Refresh the shared albums list
      window.location.reload();
    } catch (e) {
      console.error("Failed to merge anonymous shares:", e);
    } finally {
      setIsMerging(false);
    }
  };

  const handleDeclineMerge = () => {
    // Just clear the anonymous data without merging
    authService.clearAnonymousAuth();
    setShowMergeModal(false);
  };

  const openAlbumModal = (tab: "create" | "join") => {
    setAlbumModalTab(tab);
    setIsCreateModalOpen(true);
  };

  const handleCreateAlbum = async (albumData: CreateAlbumDto) => {
    const newAlbum = await createAlbum(albumData);
    if (newAlbum) {
      setIsCreateModalOpen(false);
    }
  };

  const handleJoinedAlbum = (album: { ownerId: string; albumId: string }) => {
    setIsCreateModalOpen(false);
    navigate(`/owners/${album.ownerId}/albums/${album.albumId}`);
  };

  const handleAlbumClick = (album: AlbumWithShare) => {
    navigate(`/owners/${album.ownerId}/albums/${album.id}`);
  };

  const handleDeleteAlbum = (album: AlbumWithShare) => {
    setAlbumToDelete(album);
  };

  const handleConfirmDelete = async () => {
    if (albumToDelete) {
      const success = await deleteAlbum(albumToDelete.id);
      if (success) {
        setAlbumToDelete(null);
      }
    }
  };

  const handleCancelDelete = () => {
    setAlbumToDelete(null);
  };

  const isLoading = userLoading || albumsLoading || sharedLoading;

  const filters: { key: AlbumFilter; label: string; count: number }[] = [
    { key: "all", label: "All Albums", count: albumsForAllView.length },
    { key: "my-albums", label: "My Albums", count: ownedAlbums.length },
    { key: "shared", label: "Shared with Me", count: sharedAlbumsWithDetails.length },
  ];

  const showCreateFab = hasAlbums && !isAnonymous;

  return (
    <div data-section="albums-page-root" className="jp-page min-h-screen relative overflow-hidden">
      <ErrorBoundary onError={logError}>
        <TopNavBar onLogout={onLogout} />
      </ErrorBoundary>

      <main className="relative z-10 pt-20 pb-12 sm:pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Mobile FAB for Create Album (only when albums exist and not anonymous) */}
          {showCreateFab && (
            <button
              onClick={() => openAlbumModal("create")}
              disabled={isLoading}
              className="fixed bottom-6 right-5 z-30 sm:hidden flex items-center gap-2 pl-4 pr-5 py-3.5 rounded-2xl text-white font-semibold text-sm shadow-2xl shadow-black/40 bg-[linear-gradient(30deg,var(--primary-green-dark),var(--primary-green))] focus:outline-none focus:ring-2 focus:ring-[rgba(var(--primary-green-rgb),0.5)] disabled:opacity-60"
              style={{ paddingBottom: "calc(0.875rem + env(safe-area-inset-bottom, 0px))" }}
              aria-label="Create new album"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              New Album
            </button>
          )}

          {/* Hero Section - Only show full hero when no albums */}
          {!hasAlbums ? (
            <div className="text-center mb-20">
              <div className="relative inline-block mb-8">
                <div className="absolute inset-0 jp-gradient-brand-soft rounded-3xl blur-2xl opacity-80"></div>
                <h1 className="relative text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black jp-text-gradient leading-tight tracking-tight">
                  Welcome to joint.photos
                </h1>
              </div>

              <p className="text-lg sm:text-xl text-white/75 max-w-3xl mx-auto leading-relaxed mb-12 font-normal">
                Create your first album to start organizing and sharing your precious memories
              </p>

              <div className="relative">
                {isAnonymous ? (
                  <div className="text-center">
                    <div className="mb-4 p-6 jp-card">
                      <p className="text-white/80 text-lg mb-2">
                        You're viewing as an anonymous user
                      </p>
                      <p className="text-white/60 text-sm">
                        Please log in to create your own albums
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                    <button
                      onClick={() => openAlbumModal("create")}
                      disabled={isLoading}
                      className="jp-btn jp-btn--primary jp-btn--lg"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      <span>{isLoading ? "Loading…" : "Create your first album"}</span>
                    </button>
                    <button
                      onClick={() => openAlbumModal("join")}
                      disabled={isLoading}
                      className="jp-btn jp-btn--secondary jp-btn--lg"
                    >
                      <span>Join an album</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <>
              {/* Desktop-only Create button (mobile uses the FAB) */}
              <div className="hidden sm:flex justify-end mb-8">
                {isAnonymous ? (
                  <div className="relative group">
                    <button
                      disabled
                      className="jp-btn jp-btn--ghost jp-btn--sm opacity-60"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      <span>Create New Album</span>
                    </button>
                    <div className="absolute right-0 top-full mt-2 w-64 p-3 bg-gray-900/95 backdrop-blur-xl border border-white/20 rounded-xl text-xs text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none z-10">
                      Please log in to create your own albums
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => openAlbumModal("join")}
                      disabled={isLoading}
                      className="jp-btn jp-btn--ghost jp-btn--sm"
                    >
                      <span>Join album</span>
                    </button>
                    <button
                      onClick={() => openAlbumModal("create")}
                      disabled={isLoading}
                      className="jp-btn jp-btn--primary jp-btn--sm"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      <span>Create New Album</span>
                    </button>
                  </div>
                )}
              </div>

              {/* Filter Pills */}
              <div className="flex flex-wrap gap-3 mb-8">
                {filters.map((filter) => (
                  <button
                    key={filter.key}
                    onClick={() => setActiveFilter(filter.key)}
                    className={`jp-pill ${activeFilter === filter.key ? "is-active" : ""}`}
                  >
                    {filter.key === "shared" && (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
                        />
                      </svg>
                    )}
                    <span>{filter.label}</span>
                    <span className="jp-pill__count">{filter.count}</span>
                  </button>
                ))}
              </div>
            </>
          )}

          {/* Enhanced Error Display */}
          {error && (
            <div className="mb-8 mx-auto max-w-2xl">
              <div className="bg-red-500/20 backdrop-blur-xl border border-red-400/30 text-red-100 px-8 py-6 rounded-3xl shadow-2xl flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-red-500/20 rounded-2xl flex items-center justify-center">
                    <svg className="w-6 h-6 text-red-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z"
                      />
                    </svg>
                  </div>
                  <span className="font-semibold text-lg">{error}</span>
                </div>
                <button
                  onClick={clearError}
                  className="text-red-300 hover:text-red-100 hover:bg-red-500/20 p-3 rounded-2xl transition-all duration-300"
                  aria-label="Dismiss error"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          )}

          {/* Albums Section */}
          <ErrorBoundary onError={logError}>
            <AlbumList
              albums={filteredAlbums}
              onAlbumClick={handleAlbumClick}
              onDeleteAlbum={handleDeleteAlbum}
              isLoading={isLoading}
            />
          </ErrorBoundary>

          {/* Bottom padding for FAB clearance on mobile */}
          {showCreateFab && <div className="h-24 sm:h-0" />}

          <CreateAlbumModal
            isOpen={isCreateModalOpen}
            onClose={() => setIsCreateModalOpen(false)}
            onSubmit={handleCreateAlbum}
            onJoined={handleJoinedAlbum}
            initialTab={albumModalTab}
            isLoading={isCreating}
          />

          <DeleteAlbumModal
            isOpen={!!albumToDelete}
            album={albumToDelete}
            onClose={handleCancelDelete}
            onConfirm={handleConfirmDelete}
            isDeleting={isDeleting}
          />

          <MergeSharesModal
            isOpen={showMergeModal}
            onClose={handleDeclineMerge}
            onConfirm={handleMergeShares}
            isProcessing={isMerging}
            shareCount={anonymousShareCount}
          />
        </div>
      </main>
    </div>
  );
};

export default AlbumsPage;